import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GameShell, getNextAssetSet, shuffle } from '../shared';
import brainGameLevels from '../../../lib/brainGameLevels';
import { formatGameCopy } from './gameCopy';

const { normalizeBrainGameLevel } = brainGameLevels;

const createShelfRound = (level = 1, excludeSetId = '') => {
    const assetSet = getNextAssetSet(excludeSetId);
    const difficultyIndex = level - 1;
    const shelfSize = Math.min(6, 4 + Math.floor(difficultyIndex / 4));
    const shelf = shuffle(assetSet.items).slice(0, shelfSize);
    const correct = shelf[Math.floor(Math.random() * shelf.length)];
    const distractors = shuffle(assetSet.items.filter((item) => !shelf.some((seen) => seen.id === item.id))).slice(0, 3);

    return {
        assetSet,
        correctId: correct.id,
        id: `${Date.now()}-${Math.random()}`,
        options: shuffle([correct, ...distractors]),
        previewMs: Math.max(1500, 2300 - difficultyIndex * 70),
        shelf,
    };
};

function ObjectShelfGame({ copy = {}, initialLevel, ...props }) {
    const startingLevel = normalizeBrainGameLevel('object-shelf', initialLevel);
    const timerRef = useRef(null);
    const [round, setRound] = useState(() => createShelfRound(startingLevel));
    const [phase, setPhase] = useState('preview');
    const [choice, setChoice] = useState('');
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const correct = choice && choice === round.correctId;

    const beginRound = useCallback((nextStreak = 0, excludeSetId = '') => {
        const nextLevel = normalizeBrainGameLevel('object-shelf', startingLevel + nextStreak);
        const nextRound = createShelfRound(nextLevel, excludeSetId);
        setRound(nextRound);
        setChoice('');
        setPhase('preview');
        timerRef.current = window.setTimeout(() => setPhase('answer'), nextRound.previewMs);
    }, []);

    useEffect(() => {
        timerRef.current = window.setTimeout(() => setPhase('answer'), round.previewMs);
        return () => window.clearTimeout(timerRef.current);
    }, []);

    const choose = (itemId) => {
        if (phase !== 'answer' || choice) {
            return;
        }

        const isCorrect = itemId === round.correctId;
        const nextStreak = isCorrect ? streak + 1 : 0;
        setChoice(itemId);
        setScore((current) => current + (isCorrect ? 1 : 0));
        setStreak(nextStreak);
        setPhase('feedback');
        timerRef.current = window.setTimeout(
            () => beginRound(nextStreak, round.assetSet.id),
            isCorrect ? 700 : 950
        );
    };

    return (
        <GameShell
            {...props}
            copy={copy}
            metrics={{ level: normalizeBrainGameLevel('object-shelf', startingLevel + streak), score, streak }}
            tone={correct ? 'good' : choice ? 'miss' : 'ready'}
        >
            <div className={`UFOV-brain-games__shelf-game is-${phase}`}>
                <div className="UFOV-brain-games__shelf-preview" aria-label={copy.objectShelfCue}>
                    {round.shelf.map((item) => (
                        <span className="UFOV-brain-games__shelf-card" key={`${round.id}-${item.id}`}>
                            <span className="UFOV-brain-games__shelf-card-inner">
                                <span className="UFOV-brain-games__shelf-face is-front">
                                    <img alt="" draggable="false" src={item.src} />
                                </span>
                                <span className="UFOV-brain-games__shelf-face is-back" />
                            </span>
                        </span>
                    ))}
                </div>
                <div className="UFOV-brain-games__game-cue">{copy.objectShelfCue}</div>
                <div className="UFOV-brain-games__shelf-options">
                    {round.options.map((item, index) => {
                        const picked = choice === item.id;
                        const answer = choice && item.id === round.correctId;
                        return (
                            <button
                                aria-label={formatGameCopy(copy.answerOption, { number: index + 1 })}
                                className={`${picked ? 'is-picked' : ''} ${answer ? 'is-correct' : ''}`.trim()}
                                disabled={phase !== 'answer'}
                                key={`${round.id}-option-${item.id}`}
                                onClick={() => choose(item.id)}
                                type="button"
                            >
                                <img alt="" draggable="false" src={item.src} />
                            </button>
                        );
                    })}
                </div>
            </div>
        </GameShell>
    );
}

export default ObjectShelfGame;
